// Search the price book and pick an item (pricebook.listItems): the estimate editor's, an option
// template's and a job form's items. In its own file so a page that picks items doesn't load the
// estimate editor.
import { useEffect, useState } from 'react';
import type { MemberPlan } from '@flowology/domain';
import { client } from '../api.ts';
import { Money, errorText } from '../books/ui.tsx';
import { bookPriceFor, type PricebookItem } from './job-model.ts';

/** Search the price book and pick an item; with onFree, also "Write a line in instead" (option templates leave it out). */
export function PricebookPicker({
  onPick,
  onFree,
  label = 'Add from the price book',
  plans = [],
}: {
  onPick: (item: PricebookItem) => void;
  onFree?: () => void;
  label?: string;
  /** A member's address: each item shows the price the estimate will give it. */
  plans?: readonly MemberPlan[];
}) {
  const [query, setQuery] = useState('');
  const [found, setFound] = useState<PricebookItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!query.trim()) {
      setFound(null);
      return;
    }
    const t = setTimeout(() => {
      client
        .call<{ items: PricebookItem[] }>('pricebook.listItems', { query: query.trim(), limit: 8 })
        .then((r) => {
          setFound(r.items);
          setError(null);
        })
        .catch((e: unknown) => setError(errorText(e, 'Could not search the price book.')));
    }, 200);
    return () => clearTimeout(t);
  }, [query]);
  return (
    <div className="est-pick">
      <label className="f">
        {label}
        <input
          className="field"
          type="search"
          placeholder="Name or code"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </label>
      {error && <p className="error small">{error}</p>}
      {found && (
        <ul className="found" aria-label="Pricebook items">
          {found.length ? (
            found.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className="row-btn"
                  onClick={() => {
                    onPick(item);
                    setQuery('');
                  }}
                >
                  <span className="who-col">
                    <b>{item.name}</b> <span className="muted small">{item.code}</span>
                  </span>
                  <Money cents={bookPriceFor(item, plans).priceCents} />
                </button>
              </li>
            ))
          ) : (
            <li className="muted small">Nothing in the price book matches “{query.trim()}”.</li>
          )}
        </ul>
      )}
      {onFree && (
        <div>
          <button type="button" className="btn quiet" onClick={onFree}>
            Write a line in instead
          </button>
        </div>
      )}
    </div>
  );
}
